import { useRollbar } from '@rollbar/react'
import * as fileActions from 'client/actions/fileActions'
import ErrorBox from 'client/components/core/Error'
import usePresentPage, {
  selectors as pageSelectors,
} from 'client/hooks/usePresentPage'
import {
  getIsAllFilesFetched,
  getUserFiles,
  getUserFilesByType,
} from 'client/reducers/filesReducer'
import { useFiles } from 'client/store'
import { DataFile } from 'client/store/files/filesReducer'
import { NetworkError } from 'common/errors/NetworkError'
import { useActions } from 'common/hooks/useActions'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LogArgument } from 'rollbar'
import { twJoin } from 'tailwind-merge'
import { ModalWindow, ModalWindowSizeEnum } from '../modal-window'
import { FileLibrary } from './file-library'
import { FileUploader } from './file-uploader'

const tabs = {
  upload: 'upload',
  my: 'my',
}

interface FileManagerProps {
  closeFileManager: () => void
  onInsert: (file: DataFile) => void
  mimeTypes: string[]
  fileType?: string
  getCustomFileName?: (file: DataFile) => string
  afterRemove?: (fileId: number) => void
  afterFileUpload?: (file: DataFile) => void
  validateUploadFile?: (file: File, mimeType: string) => Promise<boolean>
}

export const FileManager = ({
  fileType,
  onInsert,
  closeFileManager,
  afterRemove,
  afterFileUpload,
  validateUploadFile,
  mimeTypes = [],
  getCustomFileName,
}: FileManagerProps) => {
  const rollbar = useRollbar()
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState<DataFile | null>(null)
  const [uploadedFile, setUploadedFile] = useState<DataFile | null>(null)
  const [currentTab, setCurrentTab] = useState<
    (typeof tabs)[keyof typeof tabs]
  >(tabs.upload)
  const [hasError, setHasError] = useState(false)
  const fetchUserFiles = useActions(fileActions.fetchUserFiles)
  const removeFileAction = useActions(fileActions.removeFile)
  const setIsAllFilesFetched = useActions(fileActions.setIsAllFilesFetched)
  // const fetchCommonFiles = useActions(fileActions.fetchCommonFiles)

  const userFiles = useFiles(files =>
    fileType ? getUserFilesByType(files, fileType) : getUserFiles(files),
  )
  const isAllFilesFetched = useFiles(getIsAllFilesFetched)
  const isTemplate = usePresentPage(pageSelectors.isTemplate)
  const [uploading, setUploading] = useState(false)
  const [isFetchingFiles, setIsFetchingFiles] = useState(false)

  useEffect(() => {
    const fetchFiles = async () => {
      const isAllFilesNeeded = !fileType && !isAllFilesFetched

      if (!Object.keys(userFiles).length || isAllFilesNeeded) {
        if (isAllFilesNeeded) {
          setIsAllFilesFetched(true)
        }
        try {
          setIsFetchingFiles(true)
          await Promise.all([
            fetchUserFiles(fileType),
            // this.props.fetchCommonFiles(this.props.fileType),
          ])
        } catch (error) {
          if (error instanceof NetworkError) {
            // do nothing
          } else {
            rollbar?.error(error as LogArgument)
            setHasError(true)
          }
        } finally {
          setIsFetchingFiles(false)
        }
      }
    }
    fetchFiles()
  }, [])

  const chooseFile = (file: DataFile | null) => {
    if (!file) {
      setSelectedFile(null)
      return
    }
    setSelectedFile(prev => (prev?.id === file.id ? null : file))
  }

  const chooseTab = (tab: typeof currentTab) => () => setCurrentTab(tab)

  const insert = () => {
    if (!selectedFile && !uploadedFile) {
      return
    }

    if (currentTab === tabs.upload && uploadedFile) {
      onInsert(uploadedFile)
    } else {
      selectedFile && onInsert(selectedFile)
    }

    closeFileManager()
  }

  const [removingFileId, setRemovingFileId] = useState<number>()

  const removeFile = async (fileId: number) => {
    if (confirm(t('core.file_manager.confirm_remove_file'))) {
      setRemovingFileId(fileId)
      await removeFileAction(fileId, rollbar)
      setRemovingFileId(undefined)
      if (afterRemove) {
        afterRemove(Number(fileId))
      }
    }
    if (selectedFile && selectedFile.id === Number(fileId)) {
      setSelectedFile(null)
    }
    if (uploadedFile && uploadedFile.id === Number(fileId)) {
      setUploadedFile(null)
    }
  }

  const afterUpload = (file: DataFile) => {
    setUploadedFile(file)
    if (afterFileUpload) {
      afterFileUpload(file)
    }
  }

  return (
    <ModalWindow
      isOpened
      hideHeader
      width={602}
      size={ModalWindowSizeEnum.Small}
      onClose={closeFileManager}
      confirmButtonProps={{
        onClick: insert,
        disabled:
          currentTab === tabs.upload
            ? uploading || !uploadedFile
            : !selectedFile,
        children: t('core.file_manager.insert'),
      }}
      cancelButtonProps={{
        onClick: closeFileManager,
        children: t('global.cancel'),
      }}
    >
      <div className="flex flex-col">
        {hasError && <ErrorBox />}
        <div className="my-0 flex items-center border-border200 border-b-2 text-M">
          <div className="flex w-full translate-y-[2px] flex-nowrap">
            <button
              type="button"
              onClick={chooseTab(tabs.upload)}
              className={twJoin(
                '!border-solid cursor-pointer border-b-2 px-3 py-2',
                currentTab === tabs.upload
                  ? 'border-primary-500'
                  : 'border-transparent',
              )}
            >
              <span>{t('core.file_manager.tabs.upload')}</span>
            </button>
            <button
              type="button"
              onClick={chooseTab(tabs.my)}
              className={twJoin(
                '!border-solid cursor-pointer border-b-2 px-3 py-2',
                currentTab === tabs.my
                  ? 'border-primary-500'
                  : 'border-transparent',
              )}
            >
              <span>{t(getTitle(fileType))}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="py-4">
        <FileUploader
          show={currentTab === tabs.upload}
          mimeTypes={mimeTypes}
          afterUpload={afterUpload}
          validateUploadFile={validateUploadFile}
          removeFile={removeFile}
          userFiles={userFiles}
          removingFileId={removingFileId}
          onUploading={setUploading}
          uploading={uploading}
        />
        <FileLibrary
          isFirstFetchingFiles={isFetchingFiles}
          files={userFiles}
          detailedFile={selectedFile}
          onClick={chooseFile}
          show={currentTab === tabs.my}
          fileType={fileType}
          fetchData={fetchUserFiles}
          removeFile={removeFile}
          removingAllowed={!isTemplate}
          removingFileId={removingFileId}
        />
      </div>
    </ModalWindow>
  )
}

function getTitle(type?: string) {
  let title = 'core.file_manager.title.your_files'

  if (type === 'image') {
    title = 'core.file_manager.title.your_images'
  } else if (type === 'video') {
    title = 'core.file_manager.title.your_video'
  } else if (type === 'audio') {
    title = 'core.file_manager.title.your_audio'
  }

  return title
}
